/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/


import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import StyledTableCell from '../../../../../components/ui-elements/table/StyledTableCell';
import StyledTableRow from '../../../../../components/ui-elements/table/StyledTableRow';
import { IconButton, Pagination, Stack } from '@mui/material';
import { BsInfoCircleFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useState } from 'react';
import DialogContainer from '../../../../../components/shared/Dialog/DialogContainer'
import Loader from '../../../../../components/ui-elements/loader';
import { useCallback } from 'react';
import { getAllSplunk } from '../../../../../store/reducers/splunk/splunkAction';
import SplunkInfo from './SplunkInfo';
import { FaHistory } from 'react-icons/fa';
import SplunkHistory from './SplunkHistory';
import RefreshIcon from "@mui/icons-material/Refresh";

const SplunkList = () => {
    const [openInfo, setOpenInfo] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState("");
    const [loading, setLoading] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const dispatch = useDispatch();
    const splunkList = useSelector(state => state.splunk.splunkList);

    const fetchSplunk = useCallback(async () => {
        setLoading(true);
        try {
            await dispatch(getAllSplunk()).unwrap();
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchSplunk();
    }, [fetchSplunk]);

    const handleCloseDialog = () => {
        setOpenInfo(false);
        setOpenHistory(false);
    };

    // Calculate paginated records
    const paginatedRecords = React.useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return splunkList?.Records?.slice(startIndex, startIndex + itemsPerPage) || [];
    }, [splunkList, currentPage]);

    // Handle next/previous page
    const totalPages = Math.ceil((splunkList?.Records?.length || 0) / itemsPerPage);
    const parseSplunkResult = (result) => {
        try {
            return result ? JSON.parse(result) : {};
        } catch (error) {
            console.error("Error parsing Splunk result:", error);
            return {};
        }
    };

    return (
        <>
            {!loading ? (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="customized table">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell label="ID" />
                                <StyledTableCell label="Search Name" />
                                <StyledTableCell label="App" />
                                <StyledTableCell label="User" />
                                <StyledTableCell label="Source Type" />
                                <StyledTableCell label="Request ID" />
                                <StyledTableCell label="Process" />
                                <StyledTableCell label=" " >

                                </StyledTableCell>
                                <StyledTableCell label=" " align="right">
                                    <IconButton onClick={fetchSplunk} color="secondary"  >
                                        <RefreshIcon sx={{ fontSize: 30 }} />
                                    </IconButton>
                                </StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRecords.map((row) => {
                                const parsedResult = row?.result ? parseSplunkResult(row.result) : {};

                                return (

                                    <StyledTableRow key={row.recordID}>
                                        <StyledTableCell label={row.recordID} />
                                        <StyledTableCell label={row.search_name} />
                                        <StyledTableCell label={row.app} />
                                        <StyledTableCell label={parsedResult.user || "N/A"} />
                                        <StyledTableCell label={parsedResult.sourcetype || "N/A"} />
                                        <StyledTableCell label={parsedResult.Request_Id || "N/A"} />
                                        <StyledTableCell label={parsedResult.process || "N/A"} />
                                        <StyledTableCell align="right">
                                            <IconButton color="primary" onClick={() => {
                                                setSelectedRecordId(row.recordID);
                                                setOpenInfo(true);
                                            }}>
                                                <BsInfoCircleFill />
                                            </IconButton>
                                        </StyledTableCell>
                                        <StyledTableCell align="right">

                                            <IconButton color="primary" onClick={() => {
                                                setSelectedRecordId(row.recordID);
                                                setOpenHistory(true);
                                            }}>
                                                <FaHistory />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>);
                            })}

                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Loader />
            )}

            {/* Pagination Controls */}

            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    variant="outlined"
                    shape="rounded"
                    color="primary"
                    size="large"
                />
            </Stack>
            {/* Dialogs */}
            <DialogContainer open={openInfo} handleClose={handleCloseDialog}>
                <SplunkInfo recordId={selectedRecordId} />
            </DialogContainer>
            <DialogContainer open={openHistory} handleClose={handleCloseDialog}>
                <SplunkHistory selectedRecordId={selectedRecordId} handleClose={handleCloseDialog} />
            </DialogContainer>
        </>
    );
};

export default SplunkList;